import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const LanguagePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage, setLanguage } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  const languages = [
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
    { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'ms', name: 'Malaysian', nativeName: 'Bahasa Malaysia', flag: '🇲🇾' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' }
  ];

  const handleLanguageSelect = (language: any) => {
    setSelectedLanguage(language);
  };

  const saveLanguageSettings = () => {
    setLanguage(selectedLanguage);
    alert(`Language changed to ${selectedLanguage.nativeName}! The app interface has been updated.`);
    navigate('/settings');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Language</h1>
      </div>

      <div className="px-4 pb-8">
        {/* Current Language */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-3">
            <Globe size={24} className="text-purple-600 dark:text-purple-400" />
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">Current Language</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 font-medium" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                {currentLanguage.nativeName}
              </p>
            </div>
          </div>
        </Card>

        {/* Language Selection */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Select Language</h2>
          <div className="space-y-2">
            {languages.map((language) => (
              <Card
                key={language.code}
                onClick={() => handleLanguageSelect(language)}
                className={`p-4 cursor-pointer transition-all duration-200 ${
                  selectedLanguage.code === language.code
                    ? 'border-2 border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-lg'
                    : 'border-2 border-transparent hover:border-purple-300 dark:hover:border-purple-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{language.flag}</span>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">{language.name}</h3>
                      <p 
                        className="text-sm text-gray-600 dark:text-gray-300 font-medium" 
                        style={{ 
                          fontFamily: language.code === 'ta' ? 'Tamil Sangam MN, Noto Sans Tamil, system-ui, sans-serif' :
                                     language.code === 'zh' ? 'PingFang SC, Hiragino Sans GB, Microsoft YaHei, system-ui, sans-serif' :
                                     language.code === 'pa' ? 'Gurmukhi MN, Noto Sans Gurmukhi, system-ui, sans-serif' :
                                     'system-ui, -apple-system, sans-serif'
                        }}
                      >
                        {language.nativeName}
                      </p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedLanguage.code === language.code
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {selectedLanguage.code === language.code && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={saveLanguageSettings}
          variant="gradient"
          size="lg"
          className="w-full mb-4"
        >
          <Globe size={20} className="mr-2" />
          Apply Language Changes
        </Button>

        {/* Language Info */}
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <Globe size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">Language Support</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                πPi Dating supports multiple languages to help you connect with people from around the world. 
                Changing your language will update the entire app interface.
              </p>
              <div className="space-y-1 text-xs text-blue-600 dark:text-blue-400">
                <p>• Interface language will change immediately</p>
                <p>• Profile content remains in your original language</p>
                <p>• Translation features available for messages</p>
                <p>• Regional preferences may be updated</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Regional Settings */}
        <Card className="p-4 mt-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Regional Preferences</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Date Format</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">How dates are displayed</p>
              </div>
              <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option>DD/MM/YYYY</option>
                <option>MM/DD/YYYY</option>
                <option>YYYY-MM-DD</option>
              </select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Distance Unit</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Kilometers or miles</p>
              </div>
              <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option>Kilometers</option>
                <option>Miles</option>
              </select>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LanguagePage;